import React, { lazy, Suspense } from 'react';
import { useCan, usePermissions, useResourceParams } from '@refinedev/core';
import { Flex, Spin } from 'antd';

type RouteAccessGuardProps = {
  children: React.ReactNode;
};

type RouteAccessState = 'allow' | 'loading' | 'deny';

export const resolveRouteAccessState = ({
  shouldCheck,
  permissionsResolved,
  roles,
  accessFetching,
  accessResult,
}: {
  shouldCheck: boolean;
  permissionsResolved: boolean;
  roles: Array<unknown>;
  accessFetching: boolean;
  accessResult?: { can?: boolean };
}): RouteAccessState => {
  if (!shouldCheck) {
    return 'allow';
  }
  if (!permissionsResolved) {
    return 'loading';
  }
  // A completed empty role set is a real deny result, not a loading state.
  // Keeping it in the loading branch leaves a signed-in user on an infinite
  // spinner and prevents the route's lazy module from ever being evaluated.
  if (roles.length === 0) {
    return 'deny';
  }
  if (accessFetching || !accessResult) {
    return 'loading';
  }
  return accessResult.can === false ? 'deny' : 'allow';
};

const PageNotFound = lazy(() =>
  import('@admin-panel/pages/PageNotFound').then(module => ({
    default: module.PageNotFound,
  })),
);

const RouteAccessGuard = ({ children }: RouteAccessGuardProps) => {
  const { resource, action } = useResourceParams();
  const permissionsQuery = usePermissions({});
  const roles = Array.isArray(permissionsQuery.data) ? permissionsQuery.data : [];

  const resourceName = resource?.name;
  const resolvedAction = action ?? 'list';
  const shouldCheck = Boolean(resourceName);

  const { data, isFetching } = useCan({
    resource: resourceName,
    action: resolvedAction,
    params: { resource },
    queryOptions: {
      enabled: shouldCheck && roles.length > 0,
    },
  });

  const accessState = resolveRouteAccessState({
    shouldCheck,
    permissionsResolved: permissionsQuery.data !== undefined && !permissionsQuery.isFetching,
    roles,
    accessFetching: isFetching,
    accessResult: data,
  });

  if (accessState === 'loading') {
    return (
      <Flex
        style={{
          height: '100%',
        }}
        align="center"
        justify="center"
      >
        <Spin size="large" />
      </Flex>
    );
  }

  if (accessState === 'deny') {
    return (
      <Suspense fallback={<RouteGuardSpinner />}>
        <PageNotFound reason="unauthorized" />
      </Suspense>
    );
  }

  return children;
};

const RouteGuardSpinner = () => (
  <Flex
    style={{
      height: '100%',
    }}
    align="center"
    justify="center"
  >
    <Spin size="large" />
  </Flex>
);

export default RouteAccessGuard;
